(() => {
  'use strict';

  if (customElements.get('fitorb-kaufbereich-v2')) return;

  class FitorbPdfProduct extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;

      const source = this.querySelector('[data-fpdf-data]');
      if (!source) return;

      try {
        this.data = JSON.parse(source.textContent);
      } catch (_) {
        return;
      }

      this._connected = true;
      this.abort = new AbortController();
      this.variants = Array.isArray(this.data.variants) ? this.data.variants : [];
      this.options = Array.isArray(this.data.options) ? this.data.options : [];
      this.sizeIndex = Number.isInteger(this.data.sizeIndex) ? this.data.sizeIndex : -1;
      this.modelIndex = Number.isInteger(this.data.modelIndex) ? this.data.modelIndex : -1;
      this.colorIndex = Number.isInteger(this.data.colorIndex) ? this.data.colorIndex : -1;
      this.form = this.querySelector('[data-product-form]');
      this.idInput = this.querySelector('[data-variant-id]');
      this.strings = this.data.strings || {};
      this.settings = this.data.settings || {};
      this.busy = false;
      this.galleryIndex = 0;
      this.variantGalleryActive = false;
      this.lastGallerySource = null;
      this.lastVariantId = null;

      this.kitValues = this.sizeIndex < 0
        ? []
        : this.values(this.sizeIndex).filter(value =>
            /probier|sizing/i.test(value) || value === this.data.kitValue
          );

      this.setSelectionFromVariant(this.data.selectedVariant);

      window.addEventListener('popstate', () => {
        this.clearError();
        this.setSelectionFromVariant(
          new URL(location.href).searchParams.get('variant')
        );
        this.render();
      }, { signal: this.abort.signal });

      this.addEventListener('click', this.onClick.bind(this), {
        signal: this.abort.signal,
        capture: true
      });

      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this), {
          signal: this.abort.signal,
          capture: true
        });
      }

      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('close', () => {
          dialog.querySelectorAll('video').forEach(video => video.pause());
        }, { signal: this.abort.signal });

        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;

          const box = dialog.getBoundingClientRect();

          if (
            event.clientX < box.left ||
            event.clientX > box.right ||
            event.clientY < box.top ||
            event.clientY > box.bottom
          ) {
            dialog.close();
          }
        }, { signal: this.abort.signal });
      });

      this.setupGallery();
      this.setupSticky();
      this.render();

      this.shippingTimer = window.setInterval(
        () => this.renderShipping(this.displayVariant()),
        60000
      );

      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.stickyObserver?.disconnect();
      this.resizeObserver?.disconnect();
      clearInterval(this.shippingTimer);
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.galleryFrame);
      cancelAnimationFrame(this.variantScrollFrame);
      this._connected = false;
    }

    setSelectionFromVariant(variantId) {
      const explicit = variantId == null
        ? null
        : this.variants.find(variant =>
            String(variant.id) === String(variantId)
          );

      const first =
        explicit ||
        this.variants.find(variant => variant.available) ||
        this.variants[0];

      this.selected = first
        ? first.options.map(String)
        : this.options.map(() => null);

      if (this.sizeIndex >= 0 && !explicit) {
        this.selected[this.sizeIndex] = null;
      }

      this.path = explicit && this.sizeIndex >= 0
        ? (this.isKit(this.selected[this.sizeIndex]) ? 'kit' : 'known')
        : null;
    }

    values(index) {
      return (this.options[index]?.values || []).map(value =>
        String(
          typeof value === 'object'
            ? value.name ?? value.value ?? ''
            : value
        )
      );
    }

    isKit(value) {
      return value != null && this.kitValues.includes(String(value));
    }

    matchesVariant(variant, selection = this.selected, ignored = []) {
      return variant.options.every((value, index) =>
        ignored.includes(index) ||
        selection[index] == null ||
        String(value) === String(selection[index])
      );
    }

    exactVariant() {
      if (this.selected.some(value => value == null)) return null;

      return this.variants.find(variant =>
        this.matchesVariant(variant)
      ) || null;
    }

    displayVariant() {
      return (
        this.exactVariant() ||
        this.variants.find(variant =>
          variant.available &&
          this.matchesVariant(variant, this.selected, [this.sizeIndex])
        ) ||
        this.variants.find(variant =>
          this.matchesVariant(variant, this.selected, [this.sizeIndex])
        ) ||
        null
      );
    }

    text(name, fallback) {
      return this.strings[name] || fallback;
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach(element => {
        element.textContent = value == null ? '' : value;
      });
    }

    clearError() {
      this.querySelectorAll('[data-error]').forEach(element => {
        element.textContent = '';
        element.hidden = true;
      });
    }

    showError(message) {
      this.querySelectorAll('[data-error]').forEach(element => {
        element.textContent = message;
        element.hidden = false;
      });
    }

    onClick(event) {
      const target = event.target.closest('button,a');
      if (!target || !this.contains(target)) return;

      if (target.matches('[data-option-index][data-option-value]')) {
        event.preventDefault();

        if (!target.disabled && !this.busy) {
          this.selectOption(
            Number(target.dataset.optionIndex),
            target.dataset.optionValue
          );
        }
      } else if (target.matches('[data-size-path]')) {
        event.preventDefault();

        if (!target.disabled && !this.busy) {
          this.selectPath(target.dataset.sizePath);
        }
      } else if (target.matches('[data-atc]')) {
        event.preventDefault();
        event.stopImmediatePropagation();

        if (this.busy || target.disabled) return;

        if (!this.exactVariant()) {
          this.promptSize();
        } else {
          this.onSubmit(event);
        }
      } else if (target.matches('[data-gallery-thumb]')) {
        event.preventDefault();
        this.goToSlide(Number(target.dataset.galleryThumb));
      } else if (target.matches('[data-gallery-prev]')) {
        event.preventDefault();
        this.goToSlide(this.galleryIndex === 0 ? this.gallerySlides().length - 1 : this.galleryIndex - 1);
      } else if (target.matches('[data-gallery-next]')) {
        event.preventDefault();
        this.goToSlide((this.galleryIndex + 1) % this.gallerySlides().length);
      } else if (target.matches('[data-gallery-dot]')) {
        event.preventDefault();
        this.goToSlide(Number(target.dataset.galleryDot));
      } else if (target.matches('[data-open-dialog]')) {
        event.preventDefault();

        const dialog = document.getElementById(target.dataset.openDialog);

        if (dialog && this.contains(dialog)) {
          this.dialogOpener = target;

          if (typeof dialog.showModal === 'function') {
            dialog.showModal();
          } else {
            dialog.setAttribute('open', '');
          }
        }
      } else if (target.matches('[data-close-dialog]')) {
        event.preventDefault();

        const dialog = target.closest('dialog');

        if (typeof dialog?.close === 'function') {
          dialog.close();
        } else {
          dialog?.removeAttribute('open');
        }

        this.dialogOpener?.focus({ preventScroll: true });
      }
    }

    selectOption(index, value) {
      if (
        !Number.isInteger(index) ||
        index < 0 ||
        index >= this.options.length ||
        !this.values(index).includes(String(value))
      ) return;

      this.clearError();
      this.selected[index] = String(value);

      if (index === this.modelIndex && this.colorIndex >= 0) {
        const compatible = this.variants.some(variant =>
          this.matchesVariant(variant, this.selected, [this.sizeIndex])
        );

        if (!compatible) {
          const next =
            this.variants.find(variant =>
              variant.available &&
              String(variant.options[index]) === value
            ) ||
            this.variants.find(variant =>
              String(variant.options[index]) === value
            );

          if (next) {
            this.selected[this.colorIndex] =
              String(next.options[this.colorIndex]);
          }
        }
      }

      if (this.sizeIndex >= 0 && index !== this.sizeIndex) {
        if (this.path === 'kit') {
          this.chooseKitValue();
        } else if (
          this.selected[this.sizeIndex] != null &&
          !this.exactVariant()?.available
        ) {
          this.selected[this.sizeIndex] = null;
        }
      }

      if (index === this.sizeIndex) {
        this.path = this.isKit(value) ? 'kit' : 'known';
      }

      const visualSelection =
        index === this.modelIndex || index === this.colorIndex;

      if (visualSelection) this.variantGalleryActive = true;

      this.render(true);

      // Modellwechsel: Bild aktualisieren ohne die Seite nach oben zu scrollen.
      // Nur eine bewusste Farbwahl soll mobil zurück zur Bildergalerie führen.
      if (visualSelection) {
        const changed = this.updateGalleryImage(this.displayVariant(), true);
        if (changed && index === this.colorIndex) {
          this.scrollToVariantGallery();
        }
      }
    }

    chooseKitValue() {
      const matching = this.variants.filter(variant =>
        this.matchesVariant(variant, this.selected, [this.sizeIndex]) &&
        this.isKit(variant.options[this.sizeIndex])
      );

      const kit = matching.find(variant => variant.available) || matching[0];

      this.selected[this.sizeIndex] = kit
        ? String(kit.options[this.sizeIndex])
        : null;
    }

    selectPath(path) {
      if (this.sizeIndex < 0 || !['known', 'kit'].includes(path)) return;
      if (path === this.path) return;

      this.clearError();
      this.path = path;
      this.selected[this.sizeIndex] = null;

      if (path === 'kit') this.chooseKitValue();

      this.render(true);
    }

    promptSize() {
      this.showError(
        this.text(
          'chooseSize',
          'Bitte wähle deine Ringgröße oder das kostenlose Probierset.'
        )
      );

      const area =
        this.querySelector('[data-size-section]') ||
        this.querySelector('[data-size-pane="known"]') ||
        this.form;

      area?.scrollIntoView({
        behavior: this.reducedMotion() ? 'auto' : 'smooth',
        block: 'center'
      });

      const first = area?.querySelector(
        'button:not([disabled]):not([hidden])'
      );

      first?.focus({ preventScroll: true });
    }

    render(updateUrl = false) {
      const current = this.exactVariant();
      const display = this.displayVariant();
      const ready = Boolean(current?.available);

      this.querySelectorAll(
        '[data-option-index][data-option-value]'
      ).forEach(button => {
        const index = Number(button.dataset.optionIndex);
        const value = button.dataset.optionValue;
        const hypothetical = this.selected.slice();

        hypothetical[index] = value;

        const ignored = index === this.modelIndex
          ? this.options
              .map((_, position) => position)
              .filter(position => position !== index)
          : index === this.sizeIndex
            ? []
            : [this.sizeIndex];

        const choices = this.variants.filter(variant =>
          this.matchesVariant(variant, hypothetical, ignored)
        );

        const selected = this.selected[index] === value;

        button.hidden =
          choices.length === 0 ||
          (index === this.sizeIndex && this.isKit(value));

        button.disabled =
          this.busy || !choices.some(variant => variant.available);

        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));

        if (index === this.colorIndex) {
          let image = button.querySelector('[data-color-image]');

          const variantImage = choices.find(
            variant => variant.image?.src
          )?.image;

          const label =
            button.querySelector('[data-color-label]') ||
            button.querySelector('span');

          if (variantImage) {
            if (!image) {
              image = document.createElement('img');
              image.setAttribute('data-color-image', '');
              image.width = 160;
              image.height = 160;
              button.prepend(image);
            }

            image.hidden = false;
            image.src = variantImage.src;
            image.removeAttribute('srcset');
            image.alt =
              `${this.selected[this.modelIndex] || ''} ${value}`.trim();
          } else if (image) {
            image.hidden = true;
            image.removeAttribute('src');
            image.removeAttribute('srcset');
          }

          label?.classList.toggle(
            'fpdf__sr-only',
            Boolean(variantImage)
          );
        }
      });

      this.querySelectorAll('[data-size-path]').forEach(button => {
        const kit = button.dataset.sizePath === 'kit';

        const kitAvailable = this.variants.some(variant =>
          variant.available &&
          this.matchesVariant(variant, this.selected, [this.sizeIndex]) &&
          this.isKit(variant.options[this.sizeIndex])
        );

        button.hidden =
          this.sizeIndex < 0 ||
          (kit && this.kitValues.length === 0);

        button.disabled = this.busy || (kit && !kitAvailable);

        button.classList.toggle(
          'is-selected',
          button.dataset.sizePath === this.path
        );

        button.setAttribute(
          'aria-pressed',
          String(button.dataset.sizePath === this.path)
        );
      });

      this.querySelectorAll('[data-size-pane]').forEach(pane => {
        pane.hidden =
          this.sizeIndex < 0 ||
          pane.dataset.sizePane !== this.path;
      });

      if (this.idInput) {
        this.idInput.value = ready ? String(current.id) : '';
        this.idInput.disabled = !ready;
      }

      const price =
        display?.price_formatted ?? this.formatMoney(display?.price);

      this.setText('[data-price]', price);
      this.setText('[data-kit-price]', price);

      this.querySelectorAll(
        '[data-fpdf-comparison-price]'
      ).forEach(element => {
        element.textContent = price;
      });

      const hasDiscount = Boolean(
        display &&
        Number(display.compare_at_price) > Number(display.price)
      );

      this.setText(
        '[data-compare]',
        hasDiscount
          ? display.compare_formatted ??
            this.formatMoney(display.compare_at_price)
          : ''
      );

      this.querySelectorAll('[data-compare],[data-sale]').forEach(element => {
        element.hidden = !hasDiscount;
      });

      if (this.colorIndex >= 0) {
        this.setText('[data-color-name]', this.selected[this.colorIndex]);
      }

      let label = this.path === 'kit'
        ? this.text('kitButton', 'Ring + gratis Probierset sichern')
        : this.text('addToCart', 'Jetzt Ring sichern');

      if (!current) {
        label = this.sizeIndex >= 0 && this.selected[this.sizeIndex] == null
          ? this.text('chooseSize', 'Bitte Größe wählen')
          : this.text('unavailable', 'Nicht verfügbar');
      } else if (!current.available) {
        label = this.text('soldOut', 'Ausverkauft');
      }

      this.querySelectorAll('[data-atc]').forEach(button => {
        const labelElement =
          button.querySelector('[data-atc-label]') || button;

        labelElement.textContent =
          ready &&
          this.path === 'known' &&
          button.hasAttribute('data-main-atc')
            ? `${label} · ${price}`
            : label;

        button.disabled =
          this.busy ||
          Boolean(current && !current.available) ||
          (
            !current &&
            (
              this.sizeIndex < 0 ||
              this.selected[this.sizeIndex] != null
            )
          );

        button.setAttribute('aria-busy', String(this.busy));
      });

      this.querySelectorAll('[data-payment]').forEach(element => {
        element.hidden = !ready || this.busy;
      });

      this.renderShipping(display);
      this.updateGalleryImage(display);

      const amount = ((display?.price || 0) / 100).toFixed(2);

      this.querySelectorAll('[data-pp-message]').forEach(message => {
        if (message.getAttribute('data-pp-amount') !== amount) {
          message.setAttribute('data-pp-amount', amount);
        }
      });

      if (updateUrl) {
        const url = new URL(location.href);

        if (current) {
          url.searchParams.set('variant', current.id);
        } else {
          url.searchParams.delete('variant');
        }

        history.replaceState(history.state, '', url.toString());
      }

      if (String(current?.id || '') !== String(this.lastVariantId || '')) {
        const previousVariant = this.variants.find(variant =>
          String(variant.id) === String(this.lastVariantId)
        );

        this.lastVariantId = current?.id || null;

        this.idInput?.dispatchEvent(
          new Event('change', { bubbles: true })
        );

        this.form?.dispatchEvent(
          new CustomEvent('variant:change', {
            bubbles: true,
            detail: {
              formId: this.form.getAttribute('id'),
              variant: current,
              previousVariant
            }
          })
        );
      }

      this.updateSticky();
    }

    formatMoney(cents) {
      if (cents == null) return '';

      try {
        return new Intl.NumberFormat(
          document.documentElement.lang || 'de-DE',
          {
            style: 'currency',
            currency: this.data.currency || 'EUR'
          }
        ).format(Number(cents) / 100);
      } catch (_) {
        return (Number(cents) / 100).toFixed(2);
      }
    }

    renderShipping(variant) {
      const unavailable = variant && !variant.available;

      const stock = unavailable
        ? this.text('soldOut', 'Ausverkauft')
        : variant?.stock_text || this.settings.deliveryText || '';

      this.setText('[data-stock-text]', stock);

      this.querySelectorAll('[data-stock-dot]').forEach(dot => {
        const color = unavailable
          ? '#888888'
          : variant?.stock_color || '#6fcb97';

        if (CSS.supports('color', color)) {
          dot.style.backgroundColor = color;
        }
      });

      const fast =
        !unavailable && /1\s*[-–—]\s*3\s*tage/i.test(stock);

      const estimate = fast && Boolean(this.shippingEstimate())
        ? 'Bestelle bis 11 Uhr – der Versand erfolgt noch heute.'
        : '';

      this.setText('[data-shipping-estimate]', estimate);

      this.querySelectorAll('[data-shipping-estimate]').forEach(element => {
        element.hidden = !estimate;
      });

      this.setText(
        '[data-shipment-type]',
        this.path === 'kit'
          ? this.text('kitShipment', 'Zuerst kommt dein Probierset.')
          : this.text('ringShipment', 'Dein Ring wird direkt versendet.')
      );
    }

    shippingEstimate() {
      let parts;

      try {
        parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: this.settings.timezone || 'Europe/Berlin',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          hourCycle: 'h23'
        }).formatToParts(new Date());
      } catch (_) {
        return '';
      }

      const info = Object.fromEntries(
        parts
          .filter(part => part.type !== 'literal')
          .map(part => [part.type, Number(part.value)])
      );

      const today = new Date(
        Date.UTC(info.year, info.month - 1, info.day)
      );

      const cutoff = Number(this.settings.cutoff ?? 11);

      if (info.hour < cutoff && this.isBusinessDay(today)) {
        return this.text('today', 'Versand noch heute');
      }

      return '';
    }

    isBusinessDay(date) {
      if ([0, 6].includes(date.getUTCDay())) return false;

      const year = date.getUTCFullYear();
      const iso = date.toISOString().slice(0, 10);

      const holidays = new Set(
        String(this.settings.holidays || '')
          .split(/[\n,;]+/)
          .map(value => value.trim())
      );

      ['01-01', '05-01', '10-03', '12-25', '12-26'].forEach(day => {
        holidays.add(`${year}-${day}`);
      });

      const a = year % 19;
      const b = Math.floor(year / 100);
      const c = year % 100;
      const d = Math.floor(b / 4);
      const e = b % 4;
      const f = Math.floor((b + 8) / 25);
      const g = Math.floor((b - f + 1) / 3);
      const h = (19 * a + b - d - g + 15) % 30;
      const i = Math.floor(c / 4);
      const k = c % 4;
      const l = (32 + 2 * e + 2 * i - h - k) % 7;
      const m = Math.floor((a + 11 * h + 22 * l) / 451);

      const easter = new Date(
        Date.UTC(
          year,
          Math.floor((h + l - 7 * m + 114) / 31) - 1,
          (h + l - 7 * m + 114) % 31 + 1
        )
      );

      [-2, 1, 39, 50].forEach(offset => {
        const holiday = new Date(easter);
        holiday.setUTCDate(holiday.getUTCDate() + offset);
        holidays.add(holiday.toISOString().slice(0, 10));
      });

      return !holidays.has(iso);
    }

    setupGallery() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;

      const dots = this.querySelector('.fpdf__dots');

      this.variants.forEach(variant => {
        if (!variant.image?.src) return;

        const id = String(variant.image.id || variant.image.src);

        const exists = this.gallerySlides().some(slide =>
          !slide.classList.contains('fpdf__slide--hero') &&
          slide.dataset.mediaId === id
        );

        if (exists) return;

        const slide = document.createElement('div');
        slide.className = 'fpdf__slide';
        slide.setAttribute('data-gallery-item', '');
        slide.dataset.mediaId = id;

        const image = document.createElement('img');
        image.src = variant.image.src;
        image.alt = variant.options
          .filter((_, index) => index !== this.sizeIndex)
          .join(' – ');
        image.width = 1200;
        image.height = 1200;
        image.loading = 'lazy';
        image.style.objectFit = 'contain';

        slide.append(image);
        this.track.append(slide);

        if (dots) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.dataset.galleryDot =
            String(this.gallerySlides().length - 1);
          dot.setAttribute('aria-label', image.alt);
          dots.append(dot);
        }
      });

      this.heroSlide = this.querySelector('.fpdf__slide--hero');

      this.heroContents = this.heroSlide
        ? Array.from(this.heroSlide.children)
        : [];

      this.buildGalleryThumbnails();
      this.enableImageSwipe();

      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.galleryFrame);

        this.galleryFrame = requestAnimationFrame(() =>
          this.updateGalleryPosition()
        );
      }, { passive: true, signal: this.abort.signal });

      window.addEventListener('resize', () => {
        this.goToSlide(this.galleryIndex || 0);
      }, { signal: this.abort.signal });

      this.goToSlide(0);
    }

    enableImageSwipe() {
      // iOS Safari may cancel pointer events when its scrolling recognizer takes over.
      // Use passive touch events for a light, responsive swipe without trapping vertical scroll.
      const stage = this.track;
      let touchStart = null;
      let pointerStart = null;
      const changeSlide = (dx, dy) => {
        if (Math.abs(dx) < 24 || Math.abs(dx) <= Math.abs(dy) * 1.06) return;
        const total = this.gallerySlides().length;
        if (total < 2) return;
        const next = this.galleryIndex + (dx < 0 ? 1 : -1);
        // Stop at the ends (no surprise jump from last back to first).
        this.goToSlide(Math.max(0, Math.min(next, total - 1)));
      };
      stage.addEventListener('dragstart', event => event.preventDefault(), {
        signal: this.abort.signal
      });
      stage.addEventListener('touchstart', event => {
        if (event.touches.length !== 1 || event.target.closest('button, a, input, video[controls]')) {
          touchStart = null;
          return;
        }
        const touch = event.touches[0];
        touchStart = { id: touch.identifier, x: touch.clientX, y: touch.clientY };
      }, { passive: true, signal: this.abort.signal });
      stage.addEventListener('touchend', event => {
        if (!touchStart) return;
        const touch = Array.from(event.changedTouches).find(item => item.identifier === touchStart.id);
        if (touch) changeSlide(touch.clientX - touchStart.x, touch.clientY - touchStart.y);
        touchStart = null;
      }, { passive: true, signal: this.abort.signal });
      stage.addEventListener('touchcancel', () => { touchStart = null; }, {
        passive: true, signal: this.abort.signal
      });
      // Desktop: dragging a mouse across the main image works as well.
      stage.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch' || !event.isPrimary || event.button !== 0 ||
            event.target.closest('button, a, input, video[controls]')) return;
        pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }, { passive: true, signal: this.abort.signal });
      stage.addEventListener('pointerup', event => {
        if (!pointerStart || event.pointerId !== pointerStart.id) return;
        changeSlide(event.clientX - pointerStart.x, event.clientY - pointerStart.y);
        pointerStart = null;
      }, { passive: true, signal: this.abort.signal });
      stage.addEventListener('pointercancel', () => { pointerStart = null; }, {
        passive: true, signal: this.abort.signal
      });
    }

    buildGalleryThumbnails() {
      const viewport = this.querySelector('[data-gallery-thumbnails]');
      if (!viewport) return;
      viewport.replaceChildren();
      // Hero/first image must be large, not duplicated among thumbnails.
      this.gallerySlides().forEach((slide, index) => {
        if (index === 0) return;
        const img = slide.querySelector('img');
        if (!img) return;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fpdf-v2__thumbnail';
        button.dataset.galleryThumb = String(index);
        button.setAttribute('aria-label', `Produktbild ${index + 1} anzeigen`);
        const thumb = document.createElement('img');
        const originalSrc = img.getAttribute('src') || img.src;
        try {
          const imageUrl = new URL(originalSrc, location.href);
          if (imageUrl.hostname === 'cdn.shopify.com' ||
              imageUrl.hostname.endsWith('.cdn.shopify.com') ||
              imageUrl.pathname.includes('/cdn/shop/')) {
            imageUrl.searchParams.set('width', '160');
            imageUrl.searchParams.delete('height');
            thumb.src = imageUrl.toString();
          } else {
            thumb.src = originalSrc;
          }
        } catch (_) {
          thumb.src = originalSrc;
        }
        thumb.alt = img.alt || `Fitorb Bild ${index + 1}`;
        thumb.loading = 'lazy';
        thumb.width = 100;
        thumb.height = 100;
        button.append(thumb);
        viewport.append(button);
      });
    }

    gallerySlides() {
      return this.track
        ? Array.from(this.track.querySelectorAll('[data-gallery-item]'))
        : [];
    }

    goToSlide(index) {
      const slides = this.gallerySlides();
      if (!slides.length) return;
      const next = Math.max(0, Math.min(index, slides.length - 1));
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-current', i === next);
        slide.hidden = i !== next;
        if (i !== next) slide.querySelectorAll('video').forEach(v => v.pause());
        else slide.querySelectorAll('video[autoplay]').forEach(v => v.play()?.catch(() => {}));
      });
      this.setGalleryIndex(next, slides.length);
    }

    updateGalleryPosition() {
      if (!this.track || getComputedStyle(this.track).display === 'grid') {
        return;
      }

      const slides = this.gallerySlides();
      const left = this.track.getBoundingClientRect().left;

      let best = 0;
      let distance = Infinity;

      slides.forEach((slide, index) => {
        const gap = Math.abs(slide.getBoundingClientRect().left - left);

        if (gap < distance) {
          best = index;
          distance = gap;
        }
      });

      this.setGalleryIndex(best, slides.length);
    }

    setGalleryIndex(index, count) {
      this.galleryIndex = index;
      this.querySelectorAll('[data-gallery-thumb]').forEach(button => {
        const selected = Number(button.dataset.galleryThumb) === index;
        button.setAttribute('aria-current', String(selected));
        if (selected) {
          const viewport = this.querySelector('[data-gallery-thumbnails]');
          if (viewport) {
            const activeBounds = button.getBoundingClientRect();
            const viewportBounds = viewport.getBoundingClientRect();
            const desiredLeft = viewport.scrollLeft +
              (activeBounds.left + activeBounds.width / 2) -
              (viewportBounds.left + viewportBounds.width / 2);
            const maxLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
            const targetLeft = Math.max(0, Math.min(desiredLeft, maxLeft));
            if (Math.abs(targetLeft - viewport.scrollLeft) > 2) {
              viewport.scrollTo({
                left: targetLeft,
                behavior: this.reducedMotion() ? 'auto' : 'smooth'
              });
            }
          }
        }
      });
    }

    updateGalleryImage(variant, force = false) {
      if (!this.variantGalleryActive) return false;

      const matching = candidate =>
        this.matchesVariant(candidate, this.selected, [this.sizeIndex]);

      const selected =
        variant?.image?.src && matching(variant)
          ? variant
          : this.variants.find(candidate =>
              candidate.image?.src && matching(candidate)
            );

      if (!selected) return false;

      const id = String(selected.image.id || selected.image.src);

      const index = this.gallerySlides().findIndex(slide =>
        !slide.classList.contains('fpdf__slide--hero') &&
        slide.dataset.mediaId === id
      );

      if (index < 0) return false;

      if (force || this.lastGallerySource !== selected.image.src) {
        this.goToSlide(index);
      }

      this.lastGallerySource = selected.image.src;

      this.querySelectorAll(
        '[data-zoom-image],[data-sticky-image]'
      ).forEach(image => {
        image.src = selected.image.src;
        image.removeAttribute('srcset');
        image.alt = selected.options
          .filter((_, position) => position !== this.sizeIndex)
          .join(' – ');
      });

      return true;
    }

    scrollToVariantGallery() {
      cancelAnimationFrame(this.variantScrollFrame);

      if (!window.matchMedia('(max-width: 749px)').matches) return;

      const gallery = this.querySelector('.fpdf__gallery');
      if (!gallery) return;

      this.variantScrollFrame = requestAnimationFrame(() => {
        if (!this.isConnected) return;

        const header =
          parseFloat(
            getComputedStyle(this).getPropertyValue('--header-height')
          ) || 0;

        window.scrollTo({
          top: Math.max(
            0,
            window.scrollY +
              gallery.getBoundingClientRect().top -
              header -
              16
          ),
          behavior: this.reducedMotion() ? 'auto' : 'smooth'
        });
      });
    }

    reducedMotion() {
      return window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
    }

    setupSticky() {
      this.sticky = this.querySelector('[data-sticky]');
      this.mainAtc = this.querySelector('[data-main-atc]');

      this.footer =
        document.querySelector('#fpdf-page-footer') ||
        document.querySelector(
          '.shopify-section-group-fitorb-pdf-footer-group,' +
          '.shopify-section-group-footer-group'
        );

      if (!this.sticky || !this.mainAtc) return;

      const update = () => {
        if (this.scrollFrame) return;

        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateSticky();
        });
      };

      window.addEventListener('scroll', update, {
        passive: true,
        signal: this.abort.signal
      });

      window.addEventListener('resize', update, {
        passive: true,
        signal: this.abort.signal
      });

      if ('IntersectionObserver' in window) {
        this.stickyObserver = new IntersectionObserver(update);
        this.stickyObserver.observe(this.mainAtc);

        if (this.footer) {
          this.stickyObserver.observe(this.footer);
        }
      }
    }

    updateSticky() {
      if (!this.sticky || !this.mainAtc) return;

      const belowButton = this.mainAtc.getBoundingClientRect().bottom < 0;

      const footerVisible =
        this.footer &&
        this.footer.getBoundingClientRect().top < innerHeight;

      const shown = belowButton && !footerVisible;

      this.sticky.hidden = !shown;
      this.sticky.classList.toggle('is-visible', shown);
      this.classList.toggle('has-sticky', shown);
    }

    async onSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.busy) return;

      const variant = this.exactVariant();

      if (!variant) {
        this.promptSize();
        return;
      }

      if (!variant.available) {
        this.showError(
          this.text('soldOut', 'Diese Variante ist ausverkauft.')
        );
        return;
      }

      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      this.clearError();
      this.busy = true;
      this.render();

      const root = window.Shopify?.routes?.root || '/';
      let added = false;

      try {
        const sections = ['variant-added'];

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections }
          })
        );

        const requestedSections = [...new Set(sections)].slice(0, 5);
        const body = new FormData(this.form);

        body.set('id', String(variant.id));
        body.set('quantity', body.get('quantity') || '1');
        body.set('sections', requestedSections.join(','));
        body.set('sections_url', `${root}variants/${variant.id}`);

        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          body,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          }
        });

        added = response.ok;
        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            typeof result.description === 'string'
              ? result.description
              : this.text(
                  'error',
                  'Der Ring konnte nicht hinzugefügt werden. Bitte versuche es erneut.'
                )
          );
        }

        const cartResponse = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' }
        });

        if (!cartResponse.ok) {
          location.assign(`${root}cart`);
          return;
        }

        const cart = await cartResponse.json();
        // AMP Slidecart does not necessarily notify the theme's <cart-count> header.
        document.dispatchEvent(new CustomEvent('fitorb:cart-synced', {
          detail: { cart }
        }));
        cart.sections = result.sections || {};

        const hasSections = requestedSections.every(section =>
          typeof cart.sections[section] === 'string'
        );

        const slideCartReady =
          typeof window.SLIDECART_UPDATE === 'function' &&
          typeof window.SLIDECART_OPEN === 'function';

        const cartType =
          window.themeVariables?.settings?.cartType || 'page';

        const items = Array.isArray(result.items)
          ? result.items
          : [result];

        if (hasSections) {
          this.form.dispatchEvent(
            new CustomEvent('variant:add', {
              bubbles: true,
              detail: {
                items,
                cart,
                blockCartDrawerOpening: slideCartReady
              }
            })
          );

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: {
                baseEvent: 'variant:add',
                cart
              }
            })
          );
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh'));

          if (cartType !== 'popover') {
            this.form.dispatchEvent(
              new CustomEvent('variant:add', {
                bubbles: true,
                detail: {
                  items,
                  cart,
                  blockCartDrawerOpening: true
                }
              })
            );
          }
        }

        this.setText(
          '[data-cart-status]',
          this.text('added', 'Dein Ring liegt im Warenkorb.')
        );

        if (slideCartReady) {
          window.SLIDECART_UPDATE();
          window.SLIDECART_OPEN();
        } else if (cartType === 'page' || !hasSections) {
          location.assign(`${root}cart`);
        }
      } catch (error) {
        if (added) {
          location.assign(`${root}cart`);
        } else {
          const message =
            error instanceof TypeError || error instanceof SyntaxError
              ? this.text(
                  'error',
                  'Die Verbindung wurde unterbrochen. Prüfe deinen Warenkorb, bevor du es erneut versuchst.'
                )
              : error.message || this.text(
                  'error',
                  'Das Hinzufügen konnte nicht bestätigt werden. Bitte prüfe deinen Warenkorb.'
                );

          this.showError(message);

          this.form.dispatchEvent(
            new CustomEvent('cart:error', {
              bubbles: true,
              detail: { error: message }
            })
          );
        }
      } finally {
        this.busy = false;
        if (this.isConnected) this.render();
      }
    }
  }

  customElements.define('fitorb-kaufbereich-v2', FitorbPdfProduct);
})();